/**
 * Página de Recuperação de Senha do Sistema MEDICARE
 * Permite ao usuário solicitar redefinição de senha
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Heading,
  Link,
  Alert,
  AlertIcon,
  useColorModeValue,
  Flex,
  Card,
  CardBody,
  Divider,
  Icon,
  Image,
} from '@chakra-ui/react';
import { FiArrowLeft, FiMail } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useNotifications } from '../../contexts/ToastContext';
import { UsuariosService } from '../../services/usuarios';

interface ForgotPasswordFormData {
  email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const { showSuccess, showError } = useNotifications();
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError('');
    
    try {
      await UsuariosService.solicitarRedefinicaoSenha(data.email);
      setIsSuccess(true);
      showSuccess('E-mail de recuperação enviado com sucesso!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.mensagem || 'Erro ao enviar e-mail de recuperação';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={bgColor}
        px={4}
      >
        <Container maxW="md">
          <Card bg={cardBg} shadow="xl" borderRadius="xl">
            <CardBody p={8}>
              <VStack spacing={6}>
                {/* Ícone de sucesso */}
                <Box
                  w={16}
                  h={16}
                  bg="green.500"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiMail} color="white" boxSize={8} />
                </Box>

                <VStack spacing={4} textAlign="center">
                  <Heading size="lg" color="green.500">
                    E-mail Enviado!
                  </Heading>
                  <Text color={textColor}>
                    Enviamos um link de recuperação para:
                  </Text>
                  <Text fontWeight="bold" color="medicare.500">
                    {getValues('email')}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                    O link expira em 1 hora.
                  </Text>
                </VStack>

                <Divider />

                <Link
                  as={RouterLink}
                  to="/login"
                  color="medicare.500"
                  fontWeight="medium"
                >
                  <Icon as={FiArrowLeft} mr={2} />
                  Voltar ao login
                </Link>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Flex>
    );
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={bgColor}
      px={4}
    >
      <Container maxW="md">
        <Card bg={cardBg} shadow="xl" borderRadius="xl">
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Logo e título */}
              <VStack spacing={4}>
                <Box
                  w={64}
                  h={64}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/logo_medicare.png"
                    alt="Logo Medicare"
                    w={64}
                    h={64}
                    objectFit="contain"
                  />
                </Box>
                <VStack spacing={2}>
                  <Heading
                    size="lg"
                    color="medicare.500"
                    textAlign="center"
                  >
                    Recuperar Senha
                  </Heading>
                  <Text
                    color={textColor}
                    textAlign="center"
                  >
                    Digite seu e-mail para receber um link de recuperação
                  </Text>
                </VStack>
              </VStack>

              <Divider />

              {/* Formulário */}
              <Box w="full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack spacing={4}>
                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <FormControl isInvalid={!!errors.email}>
                      <FormLabel>E-mail</FormLabel>
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        {...register('email', {
                          required: 'E-mail é obrigatório',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'E-mail inválido',
                          },
                        })}
                      />
                      {errors.email && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.email.message}
                        </Text>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="medicare"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                      loadingText="Enviando..."
                    >
                      Enviar Link de Recuperação
                    </Button>
                  </VStack>
                </form>
              </Box>

              {/* Link para voltar */}
              <Link
                as={RouterLink}
                to="/login"
                color="medicare.500"
                fontSize="sm"
                fontWeight="medium"
              >
                <Icon as={FiArrowLeft} mr={2} />
                Voltar ao login
              </Link>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Flex>
  );
};

export default ForgotPasswordPage;